import { Document } from "~/document";
import paths from "~/paths";
import { useEffect, useMemo, useState } from "react";
import {
  ColumnDef,
  SortingState,
  getCoreRowModel,
  getPaginationRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { PaginatedResult } from "~/util/PaginatedEndpoint";
import moment from "moment";
import { DOC_PUB_DATE_FORMAT } from "~/document";
import { Link } from "react-router-dom";
import { FetcherState, TITLE_MAX_CHARS } from "./common";
import TabTable from "./TabTable";
import styles from "./styles.module.scss";
import { UserInfo } from "~/user";

const DocsTable = (props: {
  fetcher: FetcherState<Document>;
  docsResult: PaginatedResult<Document>;
  user?: UserInfo;
}) => {
  const docs = props.docsResult;
  const data = useMemo(() => docs.results as any, [docs]);
  const columns: ColumnDef<Document, any>[] = useMemo(
    () => [
      {
        header: "Title",
        accessorKey: "title",
        cell: (info) => {
          const doc = info.row.original;
          let title = doc.title;
          title = title.length > TITLE_MAX_CHARS ? `${title.substr(0, TITLE_MAX_CHARS)}...` : title;
          return (
            <div className={styles.longCell}>
              <Link to={paths.construct.docDetail(doc.protocol)}>{title}</Link>
            </div>
          );
        },
      },
      { header: "Language", accessorKey: "language_display" },
      {
        header: "Publication date",
        accessorKey: "publication_date",
        cell: (info) => (info.getValue() ? moment(info.getValue()).format(DOC_PUB_DATE_FORMAT) : "N/A"),
      },
      {
        header: "Release date",
        accessorKey: "release_date",
        cell: (info) => (info.getValue() ? moment(info.getValue()).format(DOC_PUB_DATE_FORMAT) : "N/A"),
      },
      {
        header: "Expiration date",
        accessorKey: "expiration_date",
        cell: (info) => (info.getValue() ? moment(info.getValue()).format(DOC_PUB_DATE_FORMAT) : "N/A"),
      },
      {
        header: "Documents manager",
        accessorKey: "owner_display",
        cell: (info) => {
          const owner = info.row.original.owner;
          if (+owner === props.user?.user) {
            return props.user?.username || props.user?.email || "N/A";
          }
          return info.getValue() || "N/A";
        },
      },
      {
        header: "Last modified by",
        accessorKey: "last_modified_user",
        cell: (info) => info.getValue()?.email || "N/A",
      },
      {
        header: "Organizational Unit",
        accessorKey: "organizational_unit",
        cell: (info) => info.getValue()?.name || "N/A",
      },
      { header: "Document ID", accessorKey: "protocol" },
      { header: "Status", accessorKey: "status" },
    ],
    [props.user]
  );

  const [sorting, setSorting] = useState<SortingState>([]);
  const tableInstance = useReactTable({
    data,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    manualSorting: true,
    enableMultiSort: false,
    // Third click would otherwise clear the sort in the UI while the fetched rows keep
    // the previous ordering; cycling asc <-> desc keeps header and data in step.
    enableSortingRemoval: false,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });

  useEffect(() => {
    if (sorting.length > 0) {
      const s = sorting[0];
      props.fetcher.onSortingChange([{ id: s.id, desc: s.desc }]);
    }
  }, [sorting]);

  return (
    <TabTable
      fetcher={props.fetcher}
      tableInstance={tableInstance}
      emptyTitle="No documents"
      emptyMessage="You haven't uploaded any documents"
    />
  );
};

export default DocsTable;
